import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { dataLayerPublish } from '@dhl/datalayer'

import { Button, StackColumn } from '@dhl-ui/components'
import { Page } from '@dhl-ui/page'
import { useWizardContext } from './model/context.js'
import { Wizard } from './view/components/Wizard.js'
import { stepWizardId } from './model/steps.js'
import { useGetLastCompletedProfileSetupStep } from './model/hooks.js'
import { PortalSubPage } from '~/components-page/Page/index.js'
import { FooterButtonsContainer, ScrollablePageContent } from '~/components/index.js'

type WrapperProps = {
  // eslint-disable-next-line react/no-unused-prop-types -- it is being used
  currentStep: ReactNode
}

export type SetupWizardProps = {
  onComplete?: () => void
  onStepComplete?: (name: string, index: number) => Promise<void>
}

const WizardWrapper: FC<WrapperProps> = ({ currentStep }) => {
  const {
    nextStep,
    previousStep,
    skipStep,
    goToStep,
    currentStep: { isOptional, canContinue, stepNumber, hasPreviousStep },
    meta: { title, isLoading, steps, activeSteps },
  } = useWizardContext()
  const { t } = useTranslation()
  const startStepTimestamp = useRef<number>(Date.now())
  const hasResumed = useRef(false)
  const { data: lastCompletedStep } = useGetLastCompletedProfileSetupStep()

  const publishStepAnalytics = (type: 'skip' | 'next' | 'back') => {
    const stepPrefix = steps[stepNumber].replace(/-/g, '_')
    const getTimestampInSeconds = (timestamp: number) => Math.floor(timestamp / 1000)
    const durationInSeconds = getTimestampInSeconds(Date.now()) - getTimestampInSeconds(startStepTimestamp.current)

    dataLayerPublish('CLICK', {
      id: stepWizardId,
      [`${stepPrefix}_button_action`]: type,
      [`${stepPrefix}_duration_in_seconds`]: durationInSeconds,
    })
  }

  const nextStepHandler = () => {
    publishStepAnalytics('next')

    if (stepNumber === 0 && lastCompletedStep && !hasResumed.current) {
      hasResumed.current = true
      const completedStepIndex = steps.findIndex((step) => step === lastCompletedStep.name)
      const nextStepName = activeSteps.find((activeStep) => steps.indexOf(activeStep) > completedStepIndex)

      if (nextStepName) {
        goToStep(nextStepName)
        window.scrollTo(0, 0)
      }
    } else {
      nextStep()
      window.scrollTo(0, 0)
    }
  }

  const skipStepHandler = () => {
    publishStepAnalytics('skip')
    skipStep()
  }

  const previousStepHandler = () => {
    publishStepAnalytics('back')
    previousStep()
    window.scrollTo(0, 0)
  }

  const isLastStep = steps[stepNumber] === activeSteps[activeSteps.length - 1]
  const isFirstOrLastStep = stepNumber === 0 || stepNumber === steps.length - 1
  const showBackButton = hasPreviousStep && !isFirstOrLastStep

  useEffect(() => {
    startStepTimestamp.current = Date.now()
  }, [stepNumber])

  return (
    <PortalSubPage
      data-testid={steps[stepNumber]}
      headerBackground="subtle"
      title={title}
      onBack={showBackButton ? previousStepHandler : undefined}
    >
      <Page.Content isFullView>
        <ScrollablePageContent
          sx={{
            paddingBottom: (theme) => theme.spacing(10),
          }}
        >
          {currentStep}
        </ScrollablePageContent>

        <FooterButtonsContainer>
          <StackColumn gap={2}>
            {canContinue && (
              <Button
                fullWidth
                data-testid="setup-wizard-next-button"
                disabled={isLoading}
                variant="contained"
                onClick={nextStepHandler}
              >
                {t(isLastStep ? 'portal.profileSetup.wizard.finishButton' : 'portal.profileSetup.wizard.nextButton')}
              </Button>
            )}
            {isOptional && (
              <Button
                fullWidth
                color="inherit"
                data-testid="setup-wizard-skip-button"
                disabled={isLoading}
                variant="text"
                onClick={skipStepHandler}
              >
                {t('portal.profileSetup.wizard.skipButton')}
              </Button>
            )}
          </StackColumn>
        </FooterButtonsContainer>
      </Page.Content>
    </PortalSubPage>
  )
}

type Props = SetupWizardProps & {
  children?: ReactNode | undefined
}

export const SetupWizard: FC<Props> = ({ children, onComplete, onStepComplete }) => {
  const wrapper = useCallback(({ currentStep }: WrapperProps) => <WizardWrapper currentStep={currentStep} />, [])

  return (
    <Wizard wrapper={wrapper} onComplete={onComplete} onStepComplete={onStepComplete}>
      {children}
    </Wizard>
  )
}
